import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ChevronDown,
  ExternalLink,
  Eye,
  FilterX,
  LucideIcon,
  MapPin,
  Search,
  Sparkles,
  Star,
  X,
} from 'lucide-react';
import appDataService, { Club, Pub, Shop, Series } from '../../services/appDataService';

const ACCENT = '#3fced1';
const BATCH = 24;

export type ListingItem = Club | Pub | Shop | Series;

export interface ListingFilterDef {
  key: string;
  label: string;
  icon?: LucideIcon;
  test: (item: ListingItem) => boolean;
}

export interface ListingBadge {
  key: string;
  label: string;
  icon?: LucideIcon;
}

export interface ListingCta {
  label: string;
  href: string;
}

export interface ListingExplorerProps {
  items: ListingItem[];
  loading: boolean;
  title: string;
  subtitle?: string;
  searchPlaceholder: string;
  emptyIcon: LucideIcon;
  emptyLabel: string;
  /** Basis-Pfad für Detail-Links, z. B. "/spielstaette" */
  routeBase: string;
  /** Icon für leere Bild-Platzhalter & Karten-Fallback */
  fallbackIcon: LucideIcon;
  filters?: ListingFilterDef[];
  badges?: (item: ListingItem) => ListingBadge[];
  /** Plural-Bezeichnung für Zähler, z. B. "Vereine" (Default: "Einträge") */
  countLabel?: string;
  /** Singular-Bezeichnung für Zähler, z. B. "Verein" (Default: "Eintrag") */
  countLabelSingular?: string;
  /** Zusätzlicher Suchtext pro Eintrag (z. B. Beschreibung, Kategorien) */
  searchText?: (item: ListingItem) => string;
  /** Optionaler externer Call-to-Action auf der Karte (z. B. "Zum Shop") */
  cardCta?: (item: ListingItem) => ListingCta | null;
  /** SEO */
  seoTitle: string;
  seoDescription: string;
}

type SortMode = 'recommended' | 'name' | 'views';

const idOf = (item: ListingItem): string => item.id || item._id || '';
const slugOf = (item: ListingItem): string => item.slug || '';
const sponsoredOf = (item: ListingItem): boolean =>
  'sponsored' in item ? (item as Club).sponsored === true : false;
const viewsOf = (item: ListingItem): number => {
  if ('views' in item && typeof (item as Club).views === 'number') {
    return (item as Club).views as number;
  }
  return 0;
};

const formatViews = (n: number): string => {
  try {
    return new Intl.NumberFormat('de-DE', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(n);
  } catch {
    return String(n);
  }
};

/**
 * Wiederverwendbarer, responsiver Listen-Explorer für Vereine, Spielstätten,
 * Shops und Turnierserien – Sport-App-Feeling:
 * emotionaler Hero, "Empfohlen"-Slider (sponsored), Suche, Filter-Chips,
 * Sortierung, Karten-Grid mit Hover-Effekten, "Mehr laden".
 */
const ListingExplorer: React.FC<ListingExplorerProps> = ({
  items,
  loading,
  title,
  subtitle,
  searchPlaceholder,
  emptyIcon: EmptyIcon,
  emptyLabel,
  routeBase,
  fallbackIcon,
  filters = [],
  badges,
  countLabel = 'Einträge',
  countLabelSingular = 'Eintrag',
  searchText,
  cardCta,
  seoTitle,
  seoDescription,
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const [sortMode, setSortMode] = useState<SortMode>('recommended');
  const [visibleCount, setVisibleCount] = useState(BATCH);

  const toggleFilter = (key: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setVisibleCount(BATCH);
  };

  const resetAll = () => {
    setSearch('');
    setActiveFilters(new Set());
    setVisibleCount(BATCH);
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = items.filter((item) => item && (item.id || item._id));

    if (term) {
      list = list.filter((item) => {
        const name = appDataService.formatName(item).toLowerCase();
        const address = appDataService.formatAddress(item.address).toLowerCase();
        const extra = searchText ? (searchText(item) || '').toLowerCase() : '';
        return name.includes(term) || address.includes(term) || extra.includes(term);
      });
    }

    if (activeFilters.size > 0) {
      list = list.filter((item) =>
        filters.every((f) => (activeFilters.has(f.key) ? f.test(item) : true)),
      );
    }

    if (sortMode === 'name') {
      return [...list].sort((a, b) =>
        appDataService.formatName(a).localeCompare(appDataService.formatName(b), 'de'),
      );
    }
    if (sortMode === 'views') {
      return [...list].sort((a, b) => viewsOf(b) - viewsOf(a));
    }

    // "Empfohlen zuerst": Sponsored zuerst, dann Einträge mit Bild –
    // ansonsten Backend-Reihenfolge (Qualität)
    return [...list].sort((a, b) => {
      const rank = (x: ListingItem) =>
        (sponsoredOf(x) ? 0 : 2) +
        (appDataService.getBannerUrl(x) || appDataService.getLogoUrl(x as Club) ? 0 : 1);
      return rank(a) - rank(b);
    });
  }, [items, search, activeFilters, filters, sortMode, searchText]);

  // "Empfohlen"-Reihe: gesponserte Einträge als Premium-Slider – nur ohne
  // aktive Suche/Filter, damit im Grid nichts "verschwindet"
  const showSponsoredRow = search.trim().length === 0 && activeFilters.size === 0;
  const sponsoredItems = useMemo(
    () => (showSponsoredRow ? filtered.filter(sponsoredOf).slice(0, 10) : []),
    [filtered, showSponsoredRow],
  );
  const gridItems = useMemo(
    () => (sponsoredItems.length > 0 ? filtered.filter((i) => !sponsoredOf(i)) : filtered),
    [filtered, sponsoredItems],
  );

  const visible = gridItems.slice(0, visibleCount);
  const hasMore = gridItems.length > visibleCount;
  const hasActiveQuery = search.trim().length > 0 || activeFilters.size > 0;

  const openItem = (item: ListingItem) => {
    const slug = slugOf(item);
    if (slug) navigate(`${routeBase}/${slug}`);
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
      </Helmet>

      <div className="min-h-screen bg-zinc-950">
        {/* Hero-Header */}
        <div className="relative overflow-hidden border-b border-zinc-900">
          {/* Glow-Blobs */}
          <div
            className="pointer-events-none absolute -left-28 -top-28 h-80 w-80 rounded-full blur-3xl"
            style={{ backgroundColor: 'rgba(63,206,209,0.12)' }}
          />
          <div
            className="pointer-events-none absolute -right-20 top-6 h-64 w-64 rounded-full blur-3xl"
            style={{ backgroundColor: 'rgba(63,206,209,0.07)' }}
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(70% 120% at 20% 0%, rgba(63,206,209,0.08), transparent 60%)',
            }}
          />

          <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
            {/* Anzahl-Badge */}
            <span
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wide"
              style={{
                borderColor: 'rgba(63,206,209,0.35)',
                backgroundColor: 'rgba(63,206,209,0.10)',
                color: ACCENT,
              }}
            >
              <Sparkles size={13} />
              {loading ? 'Wird geladen …' : `${items.length} ${countLabel}`}
            </span>

            <h1 className="mt-4 text-4xl font-extrabold tracking-tight sm:text-5xl">
              <span className="hero-gradient-text">{title}</span>
            </h1>
            {subtitle && (
              <p className="mt-3 max-w-2xl text-base text-gray-400 sm:text-lg">{subtitle}</p>
            )}

            {/* Suche */}
            <div className="relative mt-7 max-w-xl">
              <Search
                size={20}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setVisibleCount(BATCH);
                }}
                placeholder={searchPlaceholder}
                className="h-12 w-full rounded-2xl border border-zinc-800 bg-zinc-900 pl-12 pr-10 text-[15px] text-white shadow-lg outline-none transition-colors placeholder:text-gray-500 focus:border-[#3fced1]/60"
              />
              {search && (
                <button
                  type="button"
                  aria-label="Suche löschen"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {/* Filter-Chips + Sortierung */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {filters.map((f) => {
                const active = activeFilters.has(f.key);
                const Icon = f.icon;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => toggleFilter(f.key)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                      active
                        ? 'border-transparent text-black'
                        : 'border-zinc-700 text-gray-300 hover:border-zinc-500'
                    }`}
                    style={active ? { backgroundColor: ACCENT } : undefined}
                  >
                    {Icon && <Icon size={15} />}
                    {f.label}
                  </button>
                );
              })}

              {/* Sortier-Dropdown */}
              <div className="relative ml-auto">
                <select
                  value={sortMode}
                  onChange={(e) => {
                    setSortMode(e.target.value as SortMode);
                    setVisibleCount(BATCH);
                  }}
                  aria-label="Sortierung"
                  className="h-9 cursor-pointer appearance-none rounded-full border border-zinc-700 bg-zinc-900 pl-4 pr-9 text-sm font-medium text-gray-300 outline-none transition-colors hover:border-zinc-500 focus:border-[#3fced1]/60"
                >
                  <option value="recommended">Empfohlen zuerst</option>
                  <option value="name">Name A–Z</option>
                  <option value="views">Meiste Aufrufe</option>
                </select>
                <ChevronDown
                  size={15}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Inhalt */}
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
          {loading ? (
            <SkeletonGrid />
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div
                className="mb-5 flex h-20 w-20 items-center justify-center rounded-full border border-zinc-800 bg-zinc-900"
                style={{ boxShadow: '0 0 60px rgba(63,206,209,0.10)' }}
              >
                <EmptyIcon size={36} className="text-zinc-600" />
              </div>
              <h3 className="mb-1.5 text-xl font-bold text-white">{emptyLabel}</h3>
              <p className="max-w-sm text-gray-500">
                {hasActiveQuery
                  ? 'Versuche andere Suchbegriffe oder setze die Filter zurück.'
                  : 'Schau später noch einmal vorbei – hier tut sich ständig etwas.'}
              </p>
              {hasActiveQuery && (
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-6 inline-flex items-center gap-2 rounded-xl border border-zinc-700 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-zinc-500 hover:bg-zinc-900"
                >
                  <FilterX size={16} style={{ color: ACCENT }} />
                  Filter zurücksetzen
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Empfohlen-Reihe (Premium-Slider) */}
              {sponsoredItems.length > 0 && (
                <section className="mb-10">
                  <div className="mb-4 flex items-center gap-2.5">
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-xl"
                      style={{ backgroundColor: 'rgba(63,206,209,0.12)' }}
                    >
                      <Star size={17} style={{ color: ACCENT }} className="fill-[#3fced1]" />
                    </span>
                    <div>
                      <h2 className="text-lg font-bold text-white sm:text-xl">Empfohlen</h2>
                      <p className="text-xs text-gray-500">Unsere Partner – ausgewählt für dich</p>
                    </div>
                  </div>
                  <div className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-3 sm:-mx-6 sm:px-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {sponsoredItems.map((item) => (
                      <SponsoredCard
                        key={`sp-${slugOf(item) || idOf(item)}`}
                        item={item}
                        fallbackIcon={fallbackIcon}
                        onClick={() => openItem(item)}
                      />
                    ))}
                  </div>
                </section>
              )}

              <div className="mb-6 flex items-baseline justify-between gap-3">
                <p className="text-sm text-gray-500">
                  {filtered.length} {filtered.length === 1 ? countLabelSingular : countLabel}
                  {hasActiveQuery ? ' gefunden' : ''}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {visible.map((item) => (
                  <ListingCard
                    key={`${item.slug || idOf(item)}`}
                    item={item}
                    fallbackIcon={fallbackIcon}
                    badges={badges?.(item) ?? []}
                    cta={cardCta?.(item) ?? null}
                    onClick={() => openItem(item)}
                  />
                ))}
              </div>

              {hasMore && (
                <div className="mt-10 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((c) => c + BATCH)}
                    className="rounded-xl border border-zinc-700 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-[#3fced1]/50 hover:bg-zinc-900"
                  >
                    Mehr laden ({gridItems.length - visibleCount})
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

const SkeletonGrid: React.FC = () => (
  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
    {Array.from({ length: 6 }).map((_, i) => (
      <div
        key={i}
        className="animate-pulse overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900"
      >
        <div className="h-44 w-full bg-zinc-800" />
        <div className="space-y-3 p-4">
          <div className="h-5 w-3/4 rounded bg-zinc-800" />
          <div className="h-4 w-1/2 rounded bg-zinc-800" />
          <div className="flex gap-2 pt-2">
            <div className="h-6 w-16 rounded-md bg-zinc-800" />
            <div className="h-6 w-16 rounded-md bg-zinc-800" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Bild-Fallback (hochwertiger Gradient-Platzhalter mit Typ-Icon)
// ---------------------------------------------------------------------------

const ImageFallback: React.FC<{ icon: LucideIcon; large?: boolean }> = ({
  icon: Icon,
  large = false,
}) => (
  <div
    className="relative flex h-full w-full items-center justify-center overflow-hidden bg-gradient-to-br from-zinc-800 via-zinc-900 to-black"
  >
    <div
      className="pointer-events-none absolute inset-0"
      style={{
        background:
          'radial-gradient(60% 80% at 30% 20%, rgba(63,206,209,0.10), transparent 65%)',
      }}
    />
    <div
      className="flex items-center justify-center rounded-full border border-zinc-700/60 bg-zinc-800/60"
      style={{
        width: large ? 72 : 60,
        height: large ? 72 : 60,
        boxShadow: '0 0 40px rgba(63,206,209,0.10)',
      }}
    >
      <Icon size={large ? 32 : 26} className="text-zinc-500" />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Premium-Karte (Empfohlen-Slider)
// ---------------------------------------------------------------------------

const SponsoredCard: React.FC<{
  item: ListingItem;
  fallbackIcon: LucideIcon;
  onClick: () => void;
}> = ({ item, fallbackIcon, onClick }) => {
  const [bannerError, setBannerError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const banner = appDataService.getBannerUrl(item);
  const logo = appDataService.getLogoUrl(item as Club);
  const name = appDataService.formatName(item);
  const address = appDataService.formatAddress(item.address);
  const showBanner = banner && !bannerError;
  const showLogo = logo && !logoError;
  const clickable = !!slugOf(item);

  return (
    <div
      role="button"
      tabIndex={clickable ? 0 : -1}
      aria-label={name}
      onClick={clickable ? onClick : undefined}
      onKeyDown={(e) => {
        if (clickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      className={`group relative h-56 w-[300px] shrink-0 snap-start overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 transition-all duration-300 sm:h-60 sm:w-[340px] ${
        clickable
          ? 'cursor-pointer hover:-translate-y-1 hover:border-[#3fced1]/50 hover:shadow-[0_16px_40px_-12px_rgba(63,206,209,0.35)]'
          : ''
      }`}
    >
      {/* Banner */}
      <div className="absolute inset-0">
        {showBanner ? (
          <img
            src={banner as string}
            alt={name}
            loading="lazy"
            onError={() => setBannerError(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <ImageFallback icon={fallbackIcon} large />
        )}
        {/* Gradient-Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent" />
      </div>

      {/* Badge */}
      <span
        className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-bold text-black shadow-lg"
        style={{ backgroundColor: ACCENT }}
      >
        <Star size={12} className="fill-black" /> Empfohlen
      </span>

      {/* Inhalt unten */}
      <div className="absolute inset-x-0 bottom-0 flex items-end gap-3 p-4">
        {showLogo && (
          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-white ring-2 ring-white/25 shadow-lg">
            <img
              src={logo as string}
              alt=""
              loading="lazy"
              onError={() => setLogoError(true)}
              className="h-full w-full object-cover"
            />
          </div>
        )}
        <div className="min-w-0">
          <h3 className="line-clamp-1 text-lg font-bold text-white">{name}</h3>
          <div className="mt-0.5 flex items-center gap-1 text-sm text-gray-300">
            <MapPin size={13} className="shrink-0" style={{ color: ACCENT }} />
            <span className="line-clamp-1">{address}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Karte
// ---------------------------------------------------------------------------

const ListingCard: React.FC<{
  item: ListingItem;
  fallbackIcon: LucideIcon;
  badges: ListingBadge[];
  cta: ListingCta | null;
  onClick: () => void;
}> = ({ item, fallbackIcon, badges, cta, onClick }) => {
  const [bannerError, setBannerError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const banner = appDataService.getBannerUrl(item);
  const logo = appDataService.getLogoUrl(item as Club);
  const name = appDataService.formatName(item);
  const address = appDataService.formatAddress(item.address);
  const views = viewsOf(item);
  const showBanner = banner && !bannerError;
  const showLogo = logo && !logoError;
  const clickable = !!slugOf(item);

  return (
    <div
      role="button"
      tabIndex={clickable ? 0 : -1}
      aria-label={name}
      onClick={clickable ? onClick : undefined}
      onKeyDown={(e) => {
        if (clickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      className={`group flex flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 text-left transition-all duration-300 ${
        clickable
          ? 'cursor-pointer hover:-translate-y-1.5 hover:border-[#3fced1]/40 hover:shadow-[0_16px_40px_-12px_rgba(63,206,209,0.25)]'
          : ''
      }`}
    >
      {/* Banner */}
      <div className="relative h-44 w-full shrink-0 overflow-hidden bg-zinc-800">
        {showBanner ? (
          <img
            src={banner as string}
            alt={name}
            loading="lazy"
            onError={() => setBannerError(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <ImageFallback icon={fallbackIcon} />
        )}

        {/* sanfter Verlauf unten für Übergang zur Karte */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-zinc-900/80 to-transparent" />

        {/* Sponsored-Badge */}
        {sponsoredOf(item) && (
          <span
            className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-bold text-black shadow-lg"
            style={{ backgroundColor: ACCENT }}
          >
            <Star size={12} className="fill-black" /> Empfohlen
          </span>
        )}

        {/* Views (dezent) */}
        {views > 0 && (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-[11px] font-medium text-gray-300 backdrop-blur-sm">
            <Eye size={12} />
            {formatViews(views)}
          </span>
        )}

        {/* Logo-Overlay */}
        {showLogo && (
          <div className="absolute -bottom-6 left-4 h-14 w-14 overflow-hidden rounded-xl bg-white shadow-lg ring-2 ring-zinc-900">
            <img
              src={logo as string}
              alt=""
              loading="lazy"
              onError={() => setLogoError(true)}
              className="h-full w-full object-cover"
            />
          </div>
        )}
      </div>

      {/* Inhalt */}
      <div className={`flex flex-1 flex-col p-4 ${showLogo ? 'pt-8' : ''}`}>
        <h3 className="line-clamp-1 text-lg font-bold text-white transition-colors group-hover:text-[#3fced1]">
          {name}
        </h3>

        <div className="mt-1.5 flex items-start gap-1.5 text-sm text-gray-400">
          <MapPin size={15} className="mt-0.5 shrink-0" style={{ color: ACCENT }} />
          <span className="line-clamp-2">{address}</span>
        </div>

        {(badges.length > 0 || cta) && (
          <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-4">
            {badges.map((b) => {
              const Icon = b.icon;
              return (
                <span
                  key={b.key}
                  className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-950/60 px-2 py-1 text-[11px] font-medium text-gray-400"
                >
                  {Icon && <Icon size={12} style={{ color: ACCENT }} />}
                  {b.label}
                </span>
              );
            })}
            {cta && (
              <a
                href={cta.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold text-black transition-transform hover:scale-[1.03]"
                style={{ backgroundColor: ACCENT }}
              >
                {cta.label}
                <ExternalLink size={13} />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListingExplorer;
